import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";

// Shared collapse state so a single "Collapse All / Expand All" pair can control
// every CollapsibleSection rendered anywhere inside the provider (Demographics
// tab spans DemographicsTab, EnrollmentTimeline and GuardianAccessCard).
const CollapseCtx = createContext(null);

export function CollapseProvider({ children, defaultCollapsed = false }) {
  const [collapsed, setCollapsed] = useState({}); // id -> true means collapsed (default expanded)
  const idsRef = useRef(new Set());
  const register = useCallback((id) => {
    idsRef.current.add(id);
    // When the tab opens collapsed by default, start each newly registered
    // section collapsed unless the user has already toggled it.
    if (defaultCollapsed) {
      setCollapsed((m) => (id in m ? m : { ...m, [id]: true }));
    }
  }, [defaultCollapsed]);
  const toggle = useCallback((id) => setCollapsed((m) => ({ ...m, [id]: !m[id] })), []);
  const collapseAll = useCallback(
    () => setCollapsed(Object.fromEntries([...idsRef.current].map((id) => [id, true]))),
    []
  );
  const expandAll = useCallback(() => setCollapsed({}), []);
  return (
    <CollapseCtx.Provider value={{ collapsed, register, toggle, collapseAll, expandAll }}>
      {children}
    </CollapseCtx.Provider>
  );
}

export function CollapseAllButtons() {
  const ctx = useContext(CollapseCtx);
  if (!ctx) return null;
  return (
    <div className="flex items-center gap-2" data-testid="demographics-collapse-controls">
      <Button variant="outline" size="sm" onClick={ctx.collapseAll} data-testid="collapse-all-btn">
        Collapse All
      </Button>
      <Button variant="outline" size="sm" onClick={ctx.expandAll} data-testid="expand-all-btn">
        Expand All
      </Button>
    </div>
  );
}

// Colored header bars, echoing the color-coded families used on the Reports page.
const COLORS = {
  blue: "bg-blue-600",
  emerald: "bg-emerald-600",
  violet: "bg-violet-600",
  indigo: "bg-indigo-600",
  teal: "bg-teal-600",
  cyan: "bg-cyan-700",
  amber: "bg-amber-500",
  rose: "bg-rose-600",
  slate: "bg-slate-600",
  sky: "bg-sky-600",
  orange: "bg-orange-500",
};

export function CollapsibleSection({ id, title, color = "slate", testid, right, children }) {
  const ctx = useContext(CollapseCtx);
  const { register } = ctx || {};
  useEffect(() => { register?.(id); }, [id, register]);

  const isCollapsed = !!(ctx?.collapsed?.[id]);
  const bar = COLORS[color] || COLORS.slate;

  return (
    <div className="rounded-lg border shadow-sm overflow-hidden bg-white" data-testid={testid || `section-${id}`}>
      <div className={`w-full flex items-center justify-between gap-2 px-4 py-3 ${bar} text-white`}>
        <button
          type="button"
          onClick={() => ctx?.toggle(id)}
          className="flex flex-1 items-center gap-2 text-left font-semibold text-base transition hover:brightness-110"
          data-testid={`section-toggle-${id}`}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          {title}
        </button>
        {right ? <div>{right}</div> : null}
      </div>
      {!isCollapsed && (
        <div className="p-4 md:p-6" data-testid={`section-body-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}

export default CollapsibleSection;
